import { useState } from 'react';

// Definição do tipo para os dados do formulário
export interface AccountFormData {
  email: string;
  name: string;
  companyName: string;
  employeesNumber: string; // Changing from number to string
  businessSegment: string;
  cellphone: string;
}

// Interface para as props do modal
interface CompletionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: AccountFormData) => void;
  loading: boolean;
}

const CompletionModal = ({ isOpen, onClose, onSubmit, loading }: CompletionModalProps) => {
  // Array of options for employee numbers
  const employeeOptions = ['Autônomo', '1-10', '11-80', '81-200', '200-500', '500+'];

  // Array of options for business segments
  const businessSegmentOptions = [
    'Serviços',
    'Tecnologia',
    'Saúde',
    'Educação',
    'Varejo',
    'Finanças',
    'Imobiliário',
    'Indústria e Manufatura',
    'Entretenimento e Mídia',
    'Agronegócio',
    'Energia',
    'Transportes e Logística',
    'Turismo e Hospitalidade',
    'Seguros',
  ];

  const [formData, setFormData] = useState<AccountFormData>({
    email: '',
    name: '',
    companyName: '',
    employeesNumber: '', // Initialize as empty string
    businessSegment: '',
    cellphone: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    if (name === 'cellphone') {
      const formattedValue = formatPhoneNumber(value);
      setFormData(prev => ({ ...prev, [name]: formattedValue }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value, // Removed the parseInt for employeesNumber
      }));
    }
  };

  // Função para formatar o número de telefone
  const formatPhoneNumber = (value: string): string => {
    // Remove todos os caracteres não numéricos
    const phoneNumber = value.replace(/\D/g, '');

    // Aplica a formatação de acordo com o tamanho
    if (phoneNumber?.length <= 2) {
      return phoneNumber;
    } else if (phoneNumber?.length <= 6) {
      return `(${phoneNumber.slice(0, 2)}) ${phoneNumber.slice(2)}`;
    } else if (phoneNumber.length <= 10) {
      return `(${phoneNumber.slice(0, 2)}) ${phoneNumber.slice(2, 6)}-${phoneNumber.slice(6)}`;
    } else {
      return `(${phoneNumber.slice(0, 2)}) ${phoneNumber.slice(2, 7)}-${phoneNumber.slice(7, 11)}`;
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 sm:p-0 overflow-y-auto">
      <div className="bg-white rounded-lg p-4 sm:p-8 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <h3 className="text-xl font-bold mb-2">Complete seu cadastro</h3>
        <p className="mb-4 text-gray-600">
          Para continuar com a análise do seu contrato, precisamos de algumas informações
          adicionais.
        </p>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <input
                type="email"
                id="email"
                name="email"
                placeholder="Email"
                value={formData.email}
                onChange={handleChange}
                required
                className="mt-1 block w-full px-3 py-1 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-black"
              />
            </div>

            <div>
              <input
                type="text"
                id="name"
                name="name"
                placeholder="Nome"
                value={formData.name}
                onChange={handleChange}
                required
                className="mt-1 block w-full px-3 py-1 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-black"
              />
            </div>

            <div>
              <input
                type="text"
                id="companyName"
                name="companyName"
                placeholder="Site da empresa"
                value={formData.companyName}
                onChange={handleChange}
                className="mt-1 block w-full px-3 py-1 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-black"
              />
            </div>

            <div>
              <select
                id="employeesNumber"
                name="employeesNumber"
                value={formData.employeesNumber}
                onChange={handleChange}
                required
                className="mt-1 block w-full px-3 py-1 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-black"
              >
                <option value="" disabled>
                  Número de funcionários
                </option>
                {employeeOptions.map(option => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <select
                id="businessSegment"
                name="businessSegment"
                value={formData.businessSegment}
                onChange={handleChange}
                required
                className="mt-1 block w-full px-3 py-1 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-black"
              >
                <option value="" disabled>
                  Segmento de negócio
                </option>
                {businessSegmentOptions.map(option => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <input
                type="tel"
                id="cellphone"
                name="cellphone"
                placeholder="Celular"
                value={formData.cellphone}
                onChange={handleChange}
                pattern="(\(\d{2}\) \d{5}-\d{4}|\(\d{2}\) \d{4}-\d{4})"
                title="Formato: (00) 00000-0000 ou (00) 0000-0000"
                maxLength={16}
                required
                className="mt-1 block w-full px-3 py-1 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-black"
              />
              <span className="text-xs text-gray-500 mt-1 block">
                Formato: (00) 00000-0000 ou (00) 0000-0000
              </span>
            </div>
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300"
            >
              {loading ? 'Enviando...' : 'Concluir cadastro'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CompletionModal;
